<template>
    <multiselect
        class="apprentice-multiselect"
        v-model="selected"
        :options="options"
        :label="computedLabel"
        :track-by="trackBy"
        :placeholder="computedPlaceholder"
        :multiple="multipleSelect"
        :show-labels="showLabels"
        :disabled="disabled"
        :max="max"
        :allow-empty="allowEmpty"
        :group-label="groupLabel"
        :group-values="groupValues"
        :group-select="groupSelect"
        :deselect-label="$t('components.press_enter_to_delete')"
        :deselect-group-label="$t('components.press_enter_to_deselect')"
        :select-group-label="$t('components.press_enter_to_select_group')"
        :select-label="$t('components.press_enter_to_select')"
        :selected-label="$t('components.selected')"
        :tag-placeholder="$t('components.press_enter_to_make')"
        ref="multiselect"
        @input="input">
        <!-- Pass on all named slots -->
        <slot
            v-for="slot in Object.keys($slots)"
            :name="slot"
            :slot="slot" />

        <span slot="noOptions">{{ $t('components.no_options_found') }}</span>
        <span slot="noResult">{{ $t('components.no_results_found') }}</span>
        <span slot="maxElements">{{
            $t('components.maximum_number_of_options_reached_first_remove_a_selected_option_to_add_another', {
                max: max,
            })
        }}</span>
        <!-- Pass on all scoped slots -->
        <template
            v-for="slot in Object.keys($scopedSlots)"
            :slot="slot"
            slot-scope="scope">
            <slot
                v-bind="scope"
                :name="slot" />
        </template>
    </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
    name: 'design-2021-apprentice-multiselect-component',

    components: { Multiselect },

    props: {
        trackBy: {
            default: 'name',
            type: String,
        },
        options: {
            type: Array,
        },
        multipleSelect: {
            type: Boolean,
            default: true,
        },
        label: {
            type: String,
            required: false,
        },
        placeholder: {
            type: String,
            required: false,
        },
        selectedIn: {
            type: [Array, Object],
            default: () => [],
        },
        value: {
            type: [Array, Object],
            required: false,
            default: null,
        },
        showLabels: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        allowEmpty: {
            type: Boolean,
            required: false,
            default: false,
        },
        groupLabel: {
            type: String,
            required: false,
            default: null,
        },
        groupValues: {
            type: String,
            required: false,
            default: null,
        },
        groupSelect: {
            type: Boolean,
            required: false,
            default: false,
        },
        max: {
            type: Number,
            required: false,
            default: null,
        },
    },

    data() {
        return {
            selected: this.value ? this.value : this.selectedIn,
        };
    },

    methods: {
        input(value) {
            this.selected = value;
            this.$emit('input', value);
        },
    },

    computed: {
        computedLabel() {
            return this.label ?? this.$t('components.make_a_choice');
        },
        computedPlaceholder() {
            return this.placeholder ?? this.$t('components.make_a_choice');
        },
    },

    watch: {
        value: function (value) {
            this.selected = value;
        },
    },
};
</script>

<style scoped lang="scss">
.apprentice-multiselect ::v-deep {
    .multiselect__element {
        .multiselect__option--highlight {
            background: $info;
            color: $text-color;
        }

        .multiselect__option--selected.multiselect__option--highlight {
            background: $info;
        }

        .multiselect__option--selected,
        .multiselect__option--selected:after {
            color: $white;
        }

        .multiselect__option--selected {
            background: var(--primary);
        }

        &.with-border {
            &,
            &.multiselect--active:not(.multiselect--above) .multiselect__tags {
                border-color: var(--primary);
                border-radius: $border-radius-content;

                .multiselect__content-wrapper {
                    margin-top: 2px;
                }
            }
        }
    }

    .multiselect__tag {
        background: var(--primary);
        margin-bottom: 0;

        .multiselect__tags-wrap {
            line-height: 1;
        }

        &,
        .multiselect__tag-icon:after {
            color: $white;
        }

        .multiselect__tag-icon:after:hover {
            background: var(--primary);
        }
    }

    .multiselect__content {
        .multiselect__option--disabled {
            background-color: rgba($primary, 50%) !important;
            color: $text-muted-color !important;
            font-weight: $font-weight-medium;
            opacity: 0.5;
        }
    }

    .multiselect__select:before {
        top: 95%;
    }

    .multiselect__tags {
        padding: 13px 40px 13px 10px;
        border: none;
        background: $secondary-background-color;
        border-radius: $border-radius-content;

        .multiselect__input,
        .multiselect__single,
        .multiselect__placeholder {
            background: transparent;
            margin-bottom: 0;
        }

        input.multiselect__input::placeholder,
        .multiselect__placeholder {
            color: $text-muted-color;
        }

        input.multiselect__input:-ms-input-placeholder {
            color: $text-muted-color;
        }

        input.multiselect__input::-ms-input-placeholder {
            color: $text-muted-color;
        }
    }

    input {
        height: auto;
        border: none;
        font-size: $default-font-size;

        &,
        &:focus {
            background-color: $secondary-background-color;
        }
    }

    .multiselect__select {
        height: 100%;
        top: 0;

        &:before {
            top: calc(50% - 10px);
            color: $primary;
            border-color: $primary transparent transparent;
            display: inline-block;
        }
    }
}
</style>
